import { useEffect, useState, useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { debounce } from "lodash";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import Table from "@mui/material/Table";
import Typography from "@mui/material/Typography";
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
} from "@mui/material";
import SearchField from "./SearchField";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";

const EMPTY_FILTERS = Object.freeze({});

const CustomTable = ({
  name,
  getItemsMethod,
  renderHeader,
  renderRow,
  columnCount,
  searchLabel,
  isSearchable = false,
  filters = EMPTY_FILTERS,
  queryKeyParts = [],
  queryParams = {},
  pageResetKey,
}) => {
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState(() => {
    const initial = {};
    for (const key of Object.keys(filters)) {
      initial[key] = filters[key]?.defaultValue ?? "";
    }
    return initial;
  });
  const filterKeys = useMemo(() => Object.keys(filters), [filters]);
  const hasFilters = useMemo(() => filterKeys.length > 0, [filterKeys]);

  const queryParamsWithFilters = useMemo(() => {
    if (!hasFilters) {
      return queryParams;
    }

    const next = { ...queryParams };
    for (const key of filterKeys) {
      const value = filterValues?.[key];
      if (value === "" || value === null || value === undefined) {
        continue;
      }

      const paramName = filters[key]?.paramName ?? key;
      next[paramName] = value;
    }

    return next;
  }, [hasFilters, filterKeys, filterValues, filters, queryParams]);

  const filtersKey = useMemo(() => {
    if (!hasFilters) {
      return [];
    }
    return filterKeys.map((key) => [key, filterValues?.[key] ?? ""]);
  }, [hasFilters, filterKeys, filterValues]);

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      name,
      getItemsMethod,
      page,
      perPage,
      search,
      filtersKey,
      ...queryKeyParts,
    ],
    queryFn: () =>
      getItemsMethod(page, perPage, search, queryParamsWithFilters),
    placeholderData: keepPreviousData,
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const totalCount = useMemo(() => data?.totalCount ?? 0, [data]);

  const setSearchedDataDebounced = useMemo(
    () =>
      debounce((_text) => {
        setPage(0);
        setSearch(_text);
      }, 600),
    [],
  );

  const onPageChange = (_, newPage) => {
    setPage(newPage);
  };

  const onPerPageChange = (event) => {
    setPage(0);
    setPerPage(parseInt(event.target.value, 10));
  };

  useEffect(() => {
    if (pageResetKey !== undefined) {
      setPage(0);
    }
  }, [pageResetKey]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(totalCount / perPage) - 1);
    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [page, perPage, totalCount]);

  useEffect(() => {
    return () => {
      setSearchedDataDebounced.cancel?.();
    };
  }, [setSearchedDataDebounced]);

  useEffect(() => {
    setFilterValues((prev) => {
      const next = {};
      for (const key of filterKeys) {
        if (Object.prototype.hasOwnProperty.call(prev, key)) {
          next[key] = prev[key];
          continue;
        }
        next[key] = filters[key]?.defaultValue ?? "";
      }

      const prevKeys = Object.keys(prev);
      if (prevKeys.length !== filterKeys.length) {
        return next;
      }
      for (const key of filterKeys) {
        if (prev[key] !== next[key]) {
          return next;
        }
      }

      return prev;
    });
  }, [filterKeys, filters]);

  if (isError) {
    return <Typography variant="subtitle2">Error loading {name}</Typography>;
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {(isSearchable || hasFilters) && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            {isSearchable && (
              <SearchField
                label={searchLabel}
                onSearch={setSearchedDataDebounced}
              />
            )}

            {filterKeys.length > 0 &&
              filterKeys.map((key) => {
                const config = filters[key] ?? {};
                const {
                  type = "select",
                  label = key,
                  options = [],
                  allLabel = "All",
                  minWidth = 200,
                } = config;

                if (type !== "select") {
                  return null;
                }

                const idBase = `${name}-filter-${key}`;
                const value = filterValues?.[key] ?? "";

                return (
                  <FormControl key={key} size="small" sx={{ minWidth }}>
                    <InputLabel id={`${idBase}-label`}>{label}</InputLabel>
                    <Select
                      labelId={`${idBase}-label`}
                      id={idBase}
                      value={value}
                      label={label}
                      onChange={(e) => {
                        setPage(0);
                        setFilterValues((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }));
                      }}
                    >
                      <MenuItem value="">{allLabel}</MenuItem>
                      {options.map((opt) => {
                        const optValue =
                          typeof opt === "string" ? opt : opt.value;
                        const optLabel =
                          typeof opt === "string" ? opt : opt.label;
                        return (
                          <MenuItem key={optValue} value={optValue}>
                            {optLabel}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                );
              })}
          </Box>
        )}

        <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
          <Table size="small">
            <TableHead>{renderHeader()}</TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center">
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight="100px"
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : items.length ? (
                items.map(renderRow)
              ) : (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center">
                    <Typography variant="subtitle1" color="textSecondary">
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={perPage}
            onRowsPerPageChange={onPerPageChange}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default CustomTable;
